import React, { useEffect, useState } from "react";
import styles from './LogReports.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import Filter from "../../../components/filter/Filter";
import CustomDateRangePicker from "../../../components/customDateRangePicker/CustomDateRangePicker";
import LogReportCard from "../../../components/logReportCard/LogReportCard";
import LoadMore from "../../../components/loadMore/loadMore";
import { useToasts } from "../../../contexts/ToastContext";
import { format } from "date-fns";
import axiosClient from "../../../api/axiosClient";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";

const LogReports = () => {
  const limit = 20;
  const [selectedFilter, setSelectedFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 6)));
  const [endDate, setEndDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({ startDate, endDate });
  const [reports, setReports] = useState([]);
  const [student, setStudent] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { studentId } = useParams();
  const { showToast } = useToasts();

  useEffect(() => {
    if(startDate && endDate) {
      setDateRange({ startDate, endDate });
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const fetchData = async () => {
      if(page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadMoreLoading(true);
      }
      const params = {
        limit: limit,
        page: page,
        startDate: format(dateRange.startDate, "yyyy-MM-dd"),
        endDate: format(dateRange.endDate, "yyyy-MM-dd"),
        studentId: studentId
      };
      if(selectedFilter) {
        params.filter = selectedFilter;
      }
      await axiosClient.get(`log-reports`, {
        params: params
      })
      .then ((response) => {
        if(response.data?.student){
          setStudent(response.data.student)
        }
        if(response.data?.logReports) {
          if(page === 1) {
            setReports([]);
          }
          setReports((prevData) => {
            return [...prevData, ...response.data.logReports.map((report) => {
              return {
                id: report.id,
                date: report.date,
                data: [
                  {
                    label: 'Report ID',
                    text: report.id
                  },
                  {
                    label: 'Log Date',
                    text: format(report.date, 'MM/dd/yyyy')
                  },
                  {
                    label: 'Type',
                    text: report.type
                  },
                ],
                cta: report.status === 'Submitted' || report.status === 'Submitted Late' ? {
                  label: report.type === 'Clinical' ? 
                    (report.status === 'Submitted' ? 'View Cases' : 'Complete the Case Log') :
                    'View Details',
                  link: report.type === 'Clinical' ? 
                    `/log-reports/student/${studentId}/log-reports/${report.id}/case-logs` : 
                    `/log-reports/student/${studentId}/log-reports/${report.id}/reading-day`
                } : null,
                status: report.status,
                type: report.type
              }
            })];
          });
          setTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadMoreLoading(false);
      });
    };

    fetchData();
  }, [page, selectedFilter, showToast, dateRange, studentId]);

  const loadMore = async () => {
    setPage(page + 1);
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Log Reports', path: '/log-reports' },
            { label: `${student.firstName ?? ''} ${student.lastName ?? ''}` }
          ]}
        />
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={[
                {
                  label: 'All',
                  value: '',
                },
                {
                  label: 'Clinical Days',
                  value: 'ClinicalDay',
                },
                {
                  label: 'Reading Days',
                  value: 'ReadingDay',
                }
              ]}
              selected={selectedFilter}
              onChange={(filter) => {
                setSelectedFilter(filter);
              }}
            />
          </div>
          <CustomDateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </div>
        { reports.length > 0 ? <div className={styles.cardList}>
          {reports.map((report, index) => {
            return (
              <div className={styles.cardItem} key={`report-${index}`}>
                <LogReportCard
                  {...report}
                />
              </div>
            );
          })}
          {(isLoadMoreLoading || page < totalPages) 
            && <LoadMore isLoading={isLoadMoreLoading} onClick={loadMore} />}
        </div> : !isLoading && <div className={styles.emptyReports}>
          <p>{`${student.firstName ?? ''} ${student.lastName ?? ''}`} have no log reports at this time.</p>
        </div>}
      </div>
    </>
  )
};

export default LogReports;